// PrediccionPartidoComponent.jsx
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import ProbabilidadesPartidoComponent from './ProbabilidadesPartidoComponent';
import PartidoInfoNavigation, { PARTIDO_TABS } from '../shared/complementos/PartidoInfoNavigation';
import FixtureComparisonBarsComponent from '@/components/page-components/fixtures/fixture-comparison-bars';
import FixturePredictionSummary from '@/components/page-components/partido/datos/FixturePredictionSummary';
import FixturePenalties from '@/components/page-components/partido/datos/fixture-penalties';
import FixtureCardsPerMinutes from '@/components/page-components/partido/datos/fixture-cards-per-minute';
import FixtureGoalsCard from '@/components/page-components/partido/datos/fixture-goals-card';
import FixtureGamesPlayerComponent from '@/components/page-components/partido/datos/fixture-games-player';

// Función para interpretar predicciones de goles de forma intuitiva
const interpretarPrediccionGoles = (valor) => {
    // Verificar si es un número válido
    if (valor === null || valor === undefined || valor === '' || isNaN(valor)) {
        return '';
    }

    const num = parseFloat(valor);

    // Si es 0, no mostrar explicación
    if (num === 0) return '';

    if (num < 0) {
        // Valores negativos: redondear por debajo (Math.floor) para obtener máximo
        const maxGoles = Math.floor(Math.abs(num));
        return ` (máximo ${maxGoles} ${maxGoles === 1 ? 'gol' : 'goles'})`;
    } else {
        // Valores positivos: redondear por arriba (Math.ceil) para obtener mínimo
        const minGoles = Math.ceil(num);
        return ` (mínimo ${minGoles} ${minGoles === 1 ? 'gol' : 'goles'})`;
    }
};

const FixturePredictionSection = ({ title, children, ...props }) => (
    <Box sx={{ border: '1px solid', borderColor: 'border.main', borderRadius: 1, mb: 1, p: 1 }} {...props}>
        <Typography component={'h2'} fontSize={14}>
            {title}:
        </Typography>
        {children}
    </Box>
);

const FixturePredictionResumenComponent = ({ predictionData, homeTeam, awayTeam }) => {
    return (
        <>
            <FixturePredictionSection title="Favorito" display="flex" alignItems="center" gap={1}>
                <Typography m={0} fontWeight={500} fontSize={14}>
                    {predictionData?.predictions?.winner.name}
                </Typography>
            </FixturePredictionSection>
            <FixturePredictionSection title="Goles">
                <Typography m={0} fontWeight={500} fontSize={14}>
                    {homeTeam}: {predictionData?.predictions?.goals.home}
                    <Typography component="span" fontSize={12} color="text.secondary" ml={0.5}>
                        {interpretarPrediccionGoles(predictionData?.predictions?.goals.home)}
                    </Typography>
                </Typography>
                <Typography m={0} fontWeight={500} fontSize={14}>
                    {awayTeam}: {predictionData?.predictions?.goals.away}
                    <Typography component="span" fontSize={12} color="text.secondary" ml={0.5}>
                        {interpretarPrediccionGoles(predictionData?.predictions?.goals.away)}
                    </Typography>
                </Typography>
            </FixturePredictionSection>

            <FixturePredictionSection title="Probabilidades">
                <ProbabilidadesPartidoComponent
                    home={predictionData?.predictions?.percent.home}
                    draw={predictionData?.predictions?.percent.draw}
                    away={predictionData?.predictions?.percent.away}
                    mb={2}
                />
            </FixturePredictionSection>
            <FixtureComparisonBarsComponent fixturePrediction={predictionData} mb={1} />
        </>
    );
};

const FixturePredictionDetalleComponent = ({ fixturePrediction, includeHome, includeAway }) => (
    <>
        {fixturePrediction?.ai?.[0]?.text && (
            <>
                <Typography variant="subtitle1" fontWeight={500} mb={1} mt={2}>
                    Explicación de la IA:
                </Typography>
                <Typography variant="body1" fontSize={14}>
                    {fixturePrediction?.ai?.[0]?.text}
                </Typography>
            </>
        )}

        <Typography component={'h2'} variant="h2" fontWeight={500} mt={2} mb={1}>
            Partidos
        </Typography>
        <FixtureGamesPlayerComponent
            fixturePrediction={fixturePrediction}
            includeAway={includeAway}
            includeHome={includeHome}
        />

        <Typography component={'h2'} fontWeight={500} mt={2} mb={1}>
            Goles
        </Typography>
        <FixtureGoalsCard
            fixturePrediction={fixturePrediction}
            includeAway={includeAway}
            includeHome={includeHome}
        />

        <Typography component={'h2'} fontWeight={500} mt={2} mb={1}>
            Tarjetas
        </Typography>
        <FixtureCardsPerMinutes
            fixturePrediction={fixturePrediction}
            includeAway={includeAway}
            includeHome={includeHome}
        />

        <Typography component={'h2'} fontWeight={500} mt={2} mb={1}>
            Penaltis
        </Typography>
        <FixturePenalties
            fixturePrediction={fixturePrediction}
            includeAway={includeAway}
            includeHome={includeHome}
        />
    </>
);

const PrediccionPartidoComponent = ({ predictionData }) => {
    const fixturePrediction = predictionData;
    const includeAway = true;
    const includeHome = true;

    const homeTeam = predictionData?.fixture?.home?.name;
    const awayTeam = predictionData?.fixture?.away?.name;

    const isMobile = useMediaQuery('(max-width: 576px)');

    if (!predictionData) return null;

    if (isMobile) {
        return (
            <>
                <Typography variant="h1" sx={{ mb: 1 }}>
                    {homeTeam} vs {awayTeam}: Predicción de partido de la IA
                </Typography>
                <PartidoInfoNavigation
                    fixture={predictionData?.fixture}
                    selectedTab={PARTIDO_TABS.PREDICCION}
                />
                <FixturePredictionResumenComponent
                    predictionData={predictionData}
                    homeTeam={homeTeam}
                    awayTeam={awayTeam}
                />
                <FixturePredictionDetalleComponent
                    fixturePrediction={fixturePrediction}
                    includeAway={includeAway}
                    includeHome={includeHome}
                />
            </>
        );
    }

    return (
        <Box className="container" sx={{ backgroundColor: 'background.default' }}>
            <Typography variant="h1" sx={{ mb: 1 }}>
                {homeTeam} vs {awayTeam}: Predicción de partido de la IA
            </Typography>
            <PartidoInfoNavigation fixture={predictionData?.fixture} selectedTab={PARTIDO_TABS.PREDICCION} />
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <FixturePredictionDetalleComponent
                        fixturePrediction={fixturePrediction}
                        includeAway={includeAway}
                        includeHome={includeHome}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FixturePredictionResumenComponent
                        predictionData={predictionData}
                        homeTeam={homeTeam}
                        awayTeam={awayTeam}
                    />
                    <FixturePredictionSummary prediction={fixturePrediction} />
                </Grid>
            </Grid>
        </Box>
    );
};

export default PrediccionPartidoComponent;
